/**
 * ELF-CIRCLE — The ELF Ecosystem (design artboard `#9c`, Hub Diagram).
 *
 * Three overlapping circles over a film band: the commercial agency, the
 * nonprofit it funds, and the platform still in development. The circles sit in
 * `mix-blend-mode: screen` so their overlaps read as one shape; hovering one
 * switches it to a solid fill, grows it, and reveals its copy plus a CTA.
 * Below, the ELF-SOCIETY waitlist capture.
 *
 * Static page — imagery is bundled rather than an editable CMS slot.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import allArt from '../../../../../assets/elf/ecosystem-all.jpg';
import circleArt from '../../../../../assets/elf/ecosystem-circle.jpg';
import cityArt from '../../../../../assets/elf/ecosystem-city.jpg';
import societyArt from '../../../../../assets/elf/ecosystem-society.jpg';

type Entity = 'circle' | 'city' | 'society';

/** The picture behind the band: the three rings at rest, or the entity being looked at. */
const ART: { id: Entity | 'all'; src: string }[] = [
  { id: 'all', src: allArt },
  { id: 'circle', src: circleArt },
  { id: 'city', src: cityArt },
  { id: 'society', src: societyArt },
];

export const Ecosystem: React.FC = () => {
  const consult = useConsult();
  const [email, setEmail] = useState('');
  const [joined, setJoined] = useState(false);
  /** Which entity the pointer, focus or last tap is on — it picks the backdrop. */
  const [hot, setHot] = useState<Entity | null>(null);
  const look = (id: Entity) => ({
    onPointerEnter: (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse') setHot(id);
    },
    onPointerLeave: (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse') setHot(null);
    },
    onFocus: () => setHot(id),
    // Touch has no hover: a tap on a circle keeps its picture up.
    onClick: () => setHot(id),
  });

  const joinWaitlist = () => {
    if (!email.trim()) return;
    // The waitlist shares the consultation capture endpoint; the source tags it.
    consult?.open({ source: 'ecosystem.waitlist', title: 'ELF-SOCIETY waitlist', email });
    setJoined(true);
  };

  return (
    <ElfShell>
      <div className="elf-page">
        <div className="elf-page__panel">
          <div className="elf-page__bleed" style={{ position: 'relative', overflow: 'hidden' }}>
            {/* All four pictures stay mounted and cross-fade; see .ecoart. */}
            {ART.map((a) => (
              <img
                key={a.id}
                className="ecoart"
                src={a.src}
                alt=""
                data-on={(hot ?? 'all') === a.id ? 'true' : undefined}
              />
            ))}
            <span
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg,rgba(0,0,0,.5),rgba(0,0,0,.34) 45%,rgba(0,0,0,.72))',
              }}
            />
            <div className="elf-page__col" style={{ position: 'relative', zIndex: 2 }}>
              <ElfPageNav current="/ecosystem" onFilm />
              <div className="wnav-onfilm" style={{ padding: '22px var(--pgut) 0' }}>
                <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Ecosystem']} />
              </div>

              <div style={{ padding: '20px var(--pgut) 0', textAlign: 'center' }}>
                <span
                  className="eyebrow"
                  style={{
                    borderColor: 'rgba(255,255,255,.28)',
                    background: 'rgba(255,255,255,.1)',
                    color: 'rgba(255,255,255,.8)',
                  }}
                >
                  <i />
                  Infrastructure, not a claim
                </span>
                <h1 className="elf-page__h1 elf-page__h1--film" style={{ margin: '14px 0 0' }}>
                  The ELF ecosystem.
                </h1>
              </div>

              <div className="venn">
                <div className="venncircle v1" {...look('circle')}>
                  <span className="vlabel">
                    ELF-CIRCLE
                    <span className="vsub">Commercial</span>
                  </span>
                  <span className="vdesc">
                    Marketing, web development, events &amp; merch — this site&rsquo;s home base.
                  </span>
                  <span className="vgo">
                    <button
                      type="button"
                      className="vbtn"
                      onClick={() => consult?.open({ source: 'ecosystem.circle', title: 'Book a consultation' })}
                    >
                      Book a consultation
                    </button>
                  </span>
                </div>

                <div className="venncircle v2" {...look('city')}>
                  <span className="vlabel">
                    ELF-CITY
                    <span className="vsub">Nonprofit</span>
                  </span>
                  <span className="vdesc">
                    At-risk youth building brands in E·L·F. Funded by ELF-CIRCLE&rsquo;s success.
                  </span>
                  <span className="vgo">
                    <a
                      className="vbtn"
                      href="https://elf-city.org"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Visit ELF-CITY.org ↗
                    </a>
                  </span>
                </div>

                <div className="venncircle v3" {...look('society')}>
                  <span className="vlabel">
                    ELF-SOCIETY
                    <span className="vsub">Platform</span>
                  </span>
                  <span className="vdesc">
                    In development — a social network for creatives, globally. Coming soon.
                  </span>
                  <span className="vgo">
                    <a className="vbtn" href="#elf-society-waitlist">
                      Join the waitlist
                    </a>
                  </span>
                </div>

                <span className="venncore">E·L·F</span>
              </div>

              <div
                id="elf-society-waitlist"
                style={{ padding: '28px var(--pgut) 56px', display: 'flex', justifyContent: 'center' }}
              >
                <div className="waitcard">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="waitkicker">Coming soon — ELF-SOCIETY</span>
                    <span className="waitcopy">Early access for models, artists &amp; creative service providers.</span>
                  </div>
                  <input
                    className="fld waitfld"
                    type="email"
                    placeholder="you@studio.com"
                    value={email}
                    aria-label="Email for the ELF-SOCIETY waitlist"
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') joinWaitlist();
                    }}
                  />
                  <button type="button" className="pillcta mag waitbtn" onClick={joinWaitlist}>
                    {joined ? 'Joined' : 'Join waitlist'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link className="nextlink" to={route('/our-story')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Proof of mission</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Our Story
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Commercial success funds ELF-CITY.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <button
              type="button"
              className="nextlink"
              onClick={() => consult?.open({ source: 'ecosystem.consult', title: 'Book a consultation' })}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Start here</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Book a consultation
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Work with ELF-CIRCLE directly.
                </span>
              </div>
              <span className="arw">→</span>
            </button>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default Ecosystem;
